import { PersonDto } from "api/contract";
import { NsKey } from "enums/NsKey";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { PenIcon } from "components/icons/Icons";
import { Button } from "components/ui/Button";
import { Building2 } from "lucide-react";
import {
  formatSubsidiaryAmount,
  formatSubsidiaryCount,
  formatSubsidiaryDate
} from "features/new-actor/components/subsidiaries/subsidiaryFormatters";
import { LegalEntityFinancialsFormValues } from "features/new-actor/types/optim/legalEntityFinancialsFormValues";
import { FC, ReactNode } from "react";
import { formatSiret } from "shared/utils/formatSiret";
import { getPersonDisplayName, isLegalEntity } from "shared/utils/person";
import { LP_BORDER, LP_TEXT, LP_TYPO } from "./legalProtectionTheme";

interface LegalProtectionCompanyCardProps {
  /** Company registered on the folder. */
  company: PersonDto;
  /** Accounting data of the company as saved; missing or empty: the card says they are to be entered. */
  financials?: LegalEntityFinancialsFormValues | null;
  /** Opens the edition of the company; without it the card is read-only. */
  onEdit?: () => void;
}

/**
 * Company of a legal protection folder: the only card of the step in the main size, since the
 * whole file hangs on it. No bin: the company is not removed from the folder in this lot.
 */
export const LegalProtectionCompanyCard: FC<LegalProtectionCompanyCardProps> = ({ company, financials, onEdit }) => {
  const { t } = useDynamicTranslation([NsKey.Common]);
  const hasFinancials = Boolean(financials && financials.revenue !== '' && financials.fiscalYearEnd);

  const renderField = (label: string, value: ReactNode, tabular?: boolean) => (
    <div className="flex flex-col gap-0.5">
      <span className={`${LP_TYPO.microLabel} ${LP_TEXT.secondary}`}>{label}</span>
      <span className={`text-[15px] ${LP_TEXT.body} ${tabular ? 'tabular-nums' : ''}`}>{value}</span>
    </div>
  );

  const renderFigure = (label: string, value: string) => (
    <div className="flex flex-col gap-0.5">
      <span className={`${LP_TYPO.microLabel} ${LP_TEXT.secondary}`}>{label}</span>
      <span className={`${LP_TYPO.cardFigure} ${LP_TEXT.title}`}>{value}</span>
    </div>
  );

  return (
    <div className={`relative w-full rounded-xl border bg-white p-8 ${LP_BORDER.card}`}>
      {/* Same edit button as the borrower cards (BorrowerInsuredCard). */}
      {onEdit && (
        <div className="absolute right-4 top-4">
          <Button
            color="slate"
            round
            onClick={onEdit}
            aria-label={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.EDIT_COMPANY")}
          >
            <PenIcon className="text-black" />
          </Button>
        </div>
      )}

      <div className="flex items-start gap-5 pr-12">
        <span
          aria-hidden="true"
          className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-primary/10 text-primary"
        >
          <Building2 className="h-6 w-6" />
        </span>

        <div className="flex flex-col gap-3.5">
          <div className="flex flex-col gap-1">
            <p className={`${LP_TYPO.companyName} ${LP_TEXT.title}`}>
              {getPersonDisplayName(company)}
            </p>
            {isLegalEntity(company) && company.registrationNumber && (
              <p className={`text-[14px] tabular-nums ${LP_TEXT.secondary}`}>
                {t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.LABELS.REGISTRATION_NUMBER")}
                {' '}
                {formatSiret(company.registrationNumber)}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-x-12 gap-y-3">
            {company.email && renderField(t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.LABELS.EMAIL"), company.email)}
            {company.phone && renderField(t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.LABELS.PHONE"), company.phone, true)}
          </div>
        </div>
      </div>

      {/* Accounting data, entered with the company in its panel. */}
      <div className={`mt-6 border-t pt-5 ${LP_BORDER.rule}`}>
        {hasFinancials && financials
          ? (
            <div className="grid grid-cols-3 gap-6">
              {renderFigure(t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARY.REVENUE"), formatSubsidiaryAmount(financials.revenue))}
              {renderFigure(t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARY.EMPLOYEES"), formatSubsidiaryCount(financials.employeesCount))}
              {renderFigure(t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARY.FISCAL_YEAR_END"), formatSubsidiaryDate(financials.fiscalYearEnd))}
            </div>
          )
          : <p className={`text-[14px] ${LP_TEXT.secondary}`}>{t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.FINANCIALS_MISSING")}</p>}
      </div>
    </div>
  );
};
